import {
  createAppSelector,
  pageGroupSelectors,
  pageGroupUserSelectors,
  pageSelectors,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import { selectCurrentSectionUserId, selectEnabledSectionUsers } from "./section-user";
import type { ExtendedPageGroupUserType, NestedPageGroupType } from "./types";

import { isGroupPage } from "utils/page-utils";

export const selectAllPageGroupUsersByPageId = createAppSelector(
  [pageGroupUserSelectors.selectAll, pageGroupSelectors.filterByPageId],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pg) => pg.id));
    return pageGroupUsers.filter((pgu) => pageGroupIds.has(pgu.page_group_id));
  },
);

export const selectPageGroupUserForCurrentUserByPageId = createAppSelector(
  [selectAllPageGroupUsersByPageId, selectCurrentSectionUserId],
  (pageGroupUsers, sectionUserId) => {
    return pageGroupUsers.find((pgu) => pgu.section_user_id === sectionUserId) ?? null;
  },
);

export const selectPageGroupIdForCurrentUserByPageId = createAppSelector(
  [selectPageGroupUserForCurrentUserByPageId],
  (pageGroupUser) => {
    return pageGroupUser?.page_group_id ?? null;
  },
);

export const selectPageGroupUsersByPageId = createAppSelector(
  [
    pageSelectors.selectById,
    pageGroupUserSelectors.selectAll,
    pageGroupSelectors.filterByPageId,
    selectEnabledSectionUsers,
    userSelectors.selectEntities,
  ],
  (page, pageGroupUsers, pageGroups, enabledSectionUsers, userMap): ExtendedPageGroupUserType[] => {
    if (!page || !pageGroups || !isGroupPage(page)) {
      return [];
    }

    return pageGroupUsers.flatMap((pgu) => {
      const group = pageGroups.find((pg) => pg.id === pgu.page_group_id);
      if (!group) {
        return [];
      }

      const enabledSectionUser = enabledSectionUsers.find((cu) => cu.id === pgu.section_user_id);
      if (!enabledSectionUser) {
        return [];
      }

      const user = userMap[enabledSectionUser.user_id];
      if (!user) {
        return [];
      }

      return [
        {
          ...pgu,
          user_id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
      ];
    });
  },
);

export const selectPageGroupsWithUsersByPageId = createAppSelector(
  [pageGroupSelectors.filterByPageId, selectPageGroupUsersByPageId],
  (pageGroups, pageGroupUsers) =>
    pageGroups.map((page_group): NestedPageGroupType => {
      const pageGroupUsersForPageGroup = pageGroupUsers.filter(
        (pgu) => pgu.page_group_id === page_group.id,
      );
      return {
        ...page_group,
        page_group_users: pageGroupUsersForPageGroup,
      };
    }),
);

export const selectPageGroupUserForUser = createAppSelector(
  [
    (state, pageId?: string | null) => pageId,
    (state, pageId?: string | null, userId?: string | null) => userId,
    selectAllPageGroupUsersByPageId,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, pageGroupUsers, sectionUsers) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIds = sectionUsers
      .filter((sectionUser) => sectionUser.user_id === userId)
      .map((sectionUser) => sectionUser.id);

    const userPageGroupUser = pageGroupUsers.find((pgu) =>
      sectionUserIds.includes(pgu.section_user_id),
    );

    return userPageGroupUser ?? null;
  },
);

export const selectNestedPageGroupForUser = createAppSelector(
  [selectPageGroupsWithUsersByPageId, selectPageGroupUserForUser],
  (nestedPageGroups, pageGroupUser): NestedPageGroupType | null => {
    if (!pageGroupUser) return null;
    return nestedPageGroups.find((pg) => pg.id === pageGroupUser.page_group_id) ?? null;
  },
);

export const selectPageGroupForReadyUser = createAppSelector(
  [
    (state, pageId: string | null) => pageId,
    selectCurrentUserId,
    pageGroupSelectors.filterByPageId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
  ],
  (pageId, userId, page_groups, page_group_users, section_users) => {
    if (!pageId || !userId) {
      return null;
    }

    const sectionUserIds = section_users
      .filter((section_user) => section_user.user_id === userId)
      .map((section_user) => section_user.id);

    // Get a list of the user's page group ids
    const userGroupIds = page_group_users
      .filter(
        (group_user) => sectionUserIds.includes(group_user.section_user_id) && group_user.is_ready,
      )
      .map((group_user) => group_user.page_group_id);

    // Go through the groups for the given page and find the group for the given user
    return page_groups.find((page_group) => userGroupIds.includes(page_group.id));
  },
);

export const selectReadiedUsersInPageGroup = createAppSelector(
  [
    (state, pageGroupId: string | null) => pageGroupId,
    pageGroupUserSelectors.selectAll,
    selectEnabledSectionUsers,
    (state) => state.users,
  ],
  (pageGroupId, page_group_users, section_users, users) => {
    if (!pageGroupId) {
      return [];
    }

    // Get a list of the section_user's for this group
    const sectionUserIds = page_group_users
      .filter((group_user) => group_user.page_group_id === pageGroupId && group_user.is_ready)
      .map((group_user) => group_user.section_user_id);

    // Get a list of all users that match a section_user.user_id
    return section_users
      .filter((section_user) => sectionUserIds.includes(section_user.id))
      .map((section_user) => {
        const user = users[section_user.user_id];
        return {
          ...user,
        };
      });
  },
);

export const selectReleasedPageGroupsForCurrentSection = createAppSelector(
  [pageGroupSelectors.selectAll, pageSelectors.filterForCurrentSection],
  (pageGroups, pages) => {
    const releasedPages = pages.filter((page) => page.released_at);
    const pageIds = new Set(releasedPages.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectPageGroupUsersForCurrentSection = createAppSelector(
  [pageGroupUserSelectors.selectAll, selectReleasedPageGroupsForCurrentSection],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
    return pageGroupUsers.filter((pageGroupUser) => pageGroupIds.has(pageGroupUser.page_group_id));
  },
);
